<template>
	<div class="user-avatar">
		<SuiAvatar
			v-tooltip="showRemove ? null : tooltip"
			size="x-small"
			show-border
			:url="user.profilePicture?.mini"
			:is-external-user="user.type === UserType.Cupo"
			:initials="user.initials ?? ''"
			:color="user.hslColor ?? ''"
			:border-progress="progress > 1 ? 1 : progress"
			:border-color="progressColor"
		/>

		<SuiIcon
			v-if="showRemove"
			v-tooltip="tooltip"
			class="user-avatar__remove"
			name="icon-x-24"
		/>
	</div>
</template>

<script setup lang="ts">
import { SuiAvatar, SuiIcon } from '@developers/scoro-ui';
import {
	type UserAssignSelectAvailabilityQuery,
	type UserAssignSelectUserFragment,
	UserType,
} from '@/generated/graphql';
import { computed } from 'vue';
import { AvailabilityThresholds } from '@/constants/constants';
import { useFormatter, useTranslation } from '@/composables';

export type UserAvatarProps = {
	user: UserAssignSelectUserFragment;
	showRemove?: boolean;
	availability?: UserAssignSelectAvailabilityQuery['TaskUsersAvailability'][number];
	extraUsedAvailability?: number;
};

const props = defineProps<UserAvatarProps>();

const { t } = useTranslation();
const { formatLongDuration } = useFormatter();

const progress = computed(() => {
	if (!props.availability) {
		return 0;
	}

	return (
		(props.availability.usedAvailability +
			(props.extraUsedAvailability ?? 0)) /
		props.availability.totalAvailability
	);
});

const progressColor = computed(() => {
	if (progress.value >= AvailabilityThresholds.HEAVY_OVERBOOKED) {
		return '#DE1818';
	}

	if (progress.value >= AvailabilityThresholds.LIGHTLY_OVERBOOKED) {
		return '#EC5050';
	}

	if (progress.value >= AvailabilityThresholds.OVERBOOKED) {
		return '#FA8888';
	}

	return '#0085E0';
});

const tooltip = computed(() => {
	if (!props.availability) {
		return '';
	}

	return t('avatars_allocated_time_tooltip', {
		name: props.user.name,
		availability: formatLongDuration({
			duration:
				props.availability.totalAvailability -
				(props.availability.usedAvailability +
					(props.extraUsedAvailability ?? 0)),
			removeMinutesOnFullHours: true,
		}),
		total: formatLongDuration({
			duration: props.availability.totalAvailability,
			removeMinutesOnFullHours: true,
		}),
	});
});
</script>

<style lang="scss">
.user-avatar {
	position: relative;
	display: flex;

	&__remove {
		--sui-icon-fill: #{$neutral-00};

		opacity: var(--user-avatar-show-remove, 0);
		top: 0.25rem;
		left: 0.25rem;
		position: absolute;
		z-index: 10;
		transition: opacity 0.3s ease-out;
	}
}
</style>
